import React, { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { toast } from "sonner";

/**
 * VoiceSearchButton
 * Pulsante microfono compatto per la ricerca vocale (Web Speech API, it-IT).
 * A differenza della dettatura estesa, qui la trascrizione è "grezza" (senza comandi
 * di punteggiatura): pensata per riempire i campi di ricerca a voce.
 *
 * - Se il browser non supporta il riconoscimento vocale, il pulsante non viene mostrato
 *   (la ricerca resta comunque utilizzabile digitando).
 * - Ascolto a frase singola: dopo l'utterance si ferma automaticamente.
 *
 * Props:
 *  - onResult: (testo: string) => void  chiamato con il testo riconosciuto (trim)
 *  - lang: default "it-IT"
 *  - className, data-testid, ...
 */

// Unisce il testo riconosciuto alla query esistente con uno spazio.
export function appendSearch(prev, chunk) {
  const t = (chunk || "").trim();
  if (!t) return prev || "";
  const cur = prev || "";
  const sep = cur && !/\s$/.test(cur) ? " " : "";
  return cur + sep + t;
}

export const VoiceSearchButton = ({ onResult, lang = "it-IT", className = "", ...props }) => {
  const testId = props["data-testid"];
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const listeningRef = useRef(false);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = false;

    rec.onresult = (e) => {
      let finalChunk = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalChunk += e.results[i][0].transcript;
      }
      const t = (finalChunk || "").trim();
      if (t) onResultRef.current?.(t);
    };
    rec.onerror = (e) => {
      const err = e?.error;
      listeningRef.current = false;
      setListening(false);
      if (err === "not-allowed" || err === "service-not-allowed") {
        toast.error("Permesso microfono negato. Abilita il microfono nel browser.");
      } else if (err && err !== "no-speech" && err !== "aborted") {
        toast.error(`Ricerca vocale non disponibile (${err}).`);
      }
    };
    rec.onend = () => {
      listeningRef.current = false;
      setListening(false);
    };

    recRef.current = rec;
    return () => {
      listeningRef.current = false;
      try {
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        rec.stop();
      } catch {
        /* no-op */
      }
      recRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  if (!supported) return null;

  const toggle = () => {
    const rec = recRef.current;
    if (!rec) return;
    if (listeningRef.current) {
      try { rec.stop(); } catch { /* no-op */ }
      listeningRef.current = false;
      setListening(false);
      return;
    }
    try {
      listeningRef.current = true;
      setListening(true);
      rec.start();
    } catch {
      listeningRef.current = false;
      setListening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={listening}
      aria-label={listening ? "Ferma ricerca vocale" : "Cerca con la voce"}
      title={listening ? "Ferma" : "Cerca con la voce (italiano)"}
      data-testid={testId}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-[background-color,color,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-blue-600)] ${
        listening
          ? "bg-[color:var(--brand-red-600)] text-white animate-pulse"
          : "text-muted-foreground hover:text-[color:var(--brand-blue-700)] hover:bg-[color:var(--brand-blue-100)]"
      } ${className}`}>
      {listening ? <Square className="h-3.5 w-3.5" /> : <Mic className="h-4 w-4" />}
    </button>
  );
};

export default VoiceSearchButton;
